import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, createPage, deletePage, fetchPages } from "../lib/api-client";
import { ErrorAlert } from "../components/Alert";
import type { MatchType, Page } from "../lib/types";

const MATCH_TYPE_LABELS: Record<MatchType, string> = {
  exact: "完全一致",
  prefix: "前方一致",
  regex: "正規表現",
};

export function PagesPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const [pages, setPages] = useState<Page[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [urlPattern, setUrlPattern] = useState("");
  const [matchType, setMatchType] = useState<MatchType>("exact");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;
    setIsLoading(true);
    fetchPages(siteId)
      .then(setPages)
      .catch((err) => setError(err instanceof ApiError ? err.message : "ページ一覧の取得に失敗しました"))
      .finally(() => setIsLoading(false));
  }, [siteId]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!siteId) return;
    setCreateError(null);
    setIsCreating(true);
    try {
      const page = await createPage(siteId, { name, urlPattern, matchType });
      setPages((prev) => [...prev, page]);
      setName("");
      setUrlPattern("");
      setMatchType("exact");
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : "ページの作成に失敗しました");
    } finally {
      setIsCreating(false);
    }
  }

  async function handleDelete(pageId: string) {
    try {
      await deletePage(pageId);
      setPages((prev) => prev.filter((p) => p.id !== pageId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ページの削除に失敗しました");
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold mb-4">ページ一覧</h1>
        <ErrorAlert message={error} />
        {isLoading ? (
          <p className="text-sm text-gray-500">読み込み中...</p>
        ) : pages.length === 0 ? (
          <p className="text-sm text-gray-500">ページがまだ登録されていません</p>
        ) : (
          <table className="w-full bg-white border border-gray-200 rounded-lg text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-4 py-2 font-medium">名前</th>
                <th className="px-4 py-2 font-medium">URLパターン</th>
                <th className="px-4 py-2 font-medium">一致方式</th>
                <th className="px-4 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr key={page.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-2">{page.name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{page.urlPattern}</td>
                  <td className="px-4 py-2">{MATCH_TYPE_LABELS[page.matchType]}</td>
                  <td className="px-4 py-2 text-right space-x-3">
                    <Link to={`/pages/${page.id}/heatmap`} className="text-xs text-blue-600 hover:underline">
                      ヒートマップ
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(page.id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 max-w-md">
        <h2 className="text-base font-semibold mb-3">ページ登録</h2>
        <ErrorAlert message={createError} />
        <form onSubmit={handleCreate} className="space-y-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1" htmlFor="page-name">
              ページ名
            </label>
            <input
              id="page-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: トップLP"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1" htmlFor="page-url-pattern">
              URLパターン
            </label>
            <input
              id="page-url-pattern"
              required
              value={urlPattern}
              onChange={(e) => setUrlPattern(e.target.value)}
              placeholder="例: /lp/spring-campaign"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1" htmlFor="page-match-type">
              一致方式
            </label>
            <select
              id="page-match-type"
              value={matchType}
              onChange={(e) => setMatchType(e.target.value as MatchType)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="exact">完全一致</option>
              <option value="prefix">前方一致</option>
              <option value="regex">正規表現</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={isCreating}
            className="rounded bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isCreating ? "作成中..." : "ページを作成"}
          </button>
        </form>
      </div>
    </div>
  );
}
